
import { MetadataEntityData } from './model/metadata.entity.data';
import { MetadataEntityFieldData } from './model/metadata.entity.field.data';
import { MetadataEntityFieldType } from './model/metadata.entity.field.types';
import { getMetadataEntityFieldType } from './utilities/get.entity.field.type';
import * as Enumerable from 'linq';
import { ActivityEntityName } from '../../../entities/shared/activity.entity.name';
import { SecurityModuleName } from '../../../security/security.module';
import { EntityFieldSensitivityRegistry } from './entity.field.sensitivity.registry';


export interface MetadataEntityGroup {
    name: string;
    displayName: string;
    type: ActivityEntityName;
    groups: MetadataEntityFieldGroup[];
}

export interface MetadataEntityFieldGroup {
    name: string;
    fields: MetadataEntityFieldData[];
}

export class MetadataCatalog {

    public static readonly entities = new Map<ActivityEntityName, MetadataEntityData>();
    public static readonly modules = new Map<SecurityModuleName, MetadataEntityData[]>();

    public static registerEntity(entity: MetadataEntityData) {
        if (!entity?.type) return;
        this.entities.set(entity.type, entity);

        if (entity?.module) {
            if (this.modules.has(entity.module)) {
                const module = this.modules.get(entity.module);
                module.push(entity);
            } else {
                const module = this.modules.set(entity.module, []).get(entity.module);
                module.push(entity);
            }
        }

        EntityFieldSensitivityRegistry.registerMetadata(entity);
        this.validateAttributeFilter(entity);
    }

    private static validateAttributeFilter(entity: MetadataEntityData) {

        if (!entity.attributeFilter || !entity.fields?.length) return;

        for (const field of entity.fields) {
            if (field.filterable && !field.attributeFilterName) {
                console.warn(`[MetadataCatalog] Entity "${entity.type}" has attributeFilter enabled but field "${field.name}" is filterable without an attributeFilterName. This field will bypass the __filters index.`);
            }
        }
    }

    public static getEntity(type: ActivityEntityName): MetadataEntityData {
        return this.entities.get(type);
    }

    public static getEntityFields(type?: ActivityEntityName, entity?: MetadataEntityData): MetadataEntityGroup {

        entity = !!entity
            ? entity
            : this.entities.get(type);

        if (!entity) return null;

        const entityGroupings: MetadataEntityGroup = {
            name: entity?.name,
            displayName: entity?.displayName,
            type: entity?.type,
            groups: []
        };

        const entityGrouping: MetadataEntityFieldGroup = {
            name: entity?.name,
            fields: []
        };

        let groups: MetadataEntityFieldGroup[] = [];

        Enumerable
            .from(entity.fields)
            .forEach(field => {

                const type = getMetadataEntityFieldType(field);

                switch (type) {

                    case MetadataEntityFieldType.EmbeddedEntity: {

                        const fieldEntity = field.typeData as MetadataEntityData;

                        if (fieldEntity?.fields?.length) {

                            const fields = Enumerable
                                .from(fieldEntity.fields)
                                .select(item => {
                                    return {
                                        ...item,
                                        path: `${field.name}.${item.path}`
                                    };
                                })
                                .toArray();

                            const fieldGroup: MetadataEntityFieldGroup = {
                                name: fieldEntity.displayName,
                                fields: fields
                            };

                            groups.push(fieldGroup);
                        }

                        break;
                    }

                    case MetadataEntityFieldType.CustomFields:
                        break;

                    default: {
                        entityGrouping.fields.push(field);
                        break;
                    }

                }

            });

        entityGrouping.fields = Enumerable
            .from(entityGrouping.fields)
            .orderBy(x => x.displayName)
            .toArray();

        groups = Enumerable
            .from(groups)
            .orderBy(x => x.name)
            .select(x => {
                x.fields = Enumerable
                    .from(x.fields)
                    .orderBy(x => x.displayName)
                    .toArray();
                return x;
            })
            .toArray();

        entityGroupings.groups = [entityGrouping, ...groups];

        return entityGroupings;

    }

}
